import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { AbstractDialogInputService, RouteGuard } from '@patsnap/tahiti-core/services/abstract-dialog-service';
import { DialogManager } from '@patsnap/tahiti-core/services/dialog-manager';
function defineProperty(target, propertyKey, instance) {
    Object.defineProperty(target, propertyKey, {
        get() {
            return instance;
        },
    });
}
/**
 * Manually inject a service into a property
 */
function manualInject(target, propertyKey, bindId) {
    Object.defineProperty(target, propertyKey, {
        get() {
            return injectService(bindId);
        },
    });
}
export function createDialogInstance(component) {
    const instance = new (class extends AbstractDialogInputService {
        constructor() {
            super(...arguments);
            this.component = component;
        }
    })();
    manualInject(instance, '_dialogManager', DialogManager);
    manualInject(instance, 'routeGuard', RouteGuard);
    return instance;
}
/**
 * Create a dialog service based on a component
 * @example
 * ```typescript
 * // prepare the container
 * container.bind(DialogManager).to(DialogManager);
 * container.bind(RouteGuard).toConstantValue(router);
 *
 * container.bind('SampleDialogService').toDynamicValue(
 *      createDialog(() => import('@/path/to/SampleDialog.vue'))
 * )
 *
 * // inject the dialog service
 * class SampleService {
 *     constructor(
 *          @inject('SampleDialogService')
 *          private readonly _sampleDialogService: AbstractDialogInputService<{ title }, string>;
 *     ){}
 *
 *     private _onReceived(data: string, next: () => void) {}
 *
 *     private _onAborted() {}
 *
 *     doSomething() {
 *          this._sampleDialogService.onReceived(this._onReceived.bind(this));
 *          this._sampleDialogService.onAborted(this._onAborted.bind(this));
 *          this._sampleDialogService.request({ title: 'Hello' });
 *     }
 * }
 * ```
 */
export function createDialog(component) {
    return () => createDialogInstance(component);
}
const dialogMetadataKey = Symbol('dialog');
/**
 * Decorator to inject a dialog service into a property
 * @param value
 * @returns
 */
export function dialog(value) {
    function attributeDecorator(target, propertyKey) {
        let instance = Reflect.getOwnMetadata(dialogMetadataKey, target, propertyKey);
        if (!instance) {
            instance = createDialogInstance(value);
            Reflect.defineMetadata(dialogMetadataKey, instance, target, propertyKey);
        }
        defineProperty(target, propertyKey, instance);
    }
    return attributeDecorator;
}
